import type { LoaderFunctionArgs } from "@remix-run/node";
import { redirect } from "@remix-run/node";
import styles from "./styles.module.css";

export const loader = async ({ request }: LoaderFunctionArgs) => {
  const url = new URL(request.url);

  // If shop param exists, redirect to app (Shopify-initiated flow)
  if (url.searchParams.get("shop")) {
    throw redirect(`/app?${url.searchParams.toString()}`);
  }

  return null;
};

export default function App() {

  return (
    <div className={styles.page}>
      {/* Nav */}
      <header className={styles.nav}>
        <span className={styles.logo}>
          <img src="/taglow_logo.png" alt="TagFlow" width="32" height="32" />
          TagFlow
        </span>
        <a
          href="https://apps.shopify.com/"
          className={styles.navCta}
          target="_blank"
          rel="noreferrer"
        >
          Add to Shopify
        </a>
      </header>

      {/* Hero */}
      <section className={styles.hero}>
        <div className={styles.heroInner}>
          <div className={styles.badge}>Shopify App</div>
          <h1 className={styles.heroHeading}>
            Automatically tag orders<br />with smart IF/THEN rules
          </h1>
          <p className={styles.heroSub}>
            No code. No manual work. TagFlow evaluates every order the moment it's
            created and applies your tags instantly.
          </p>
          <a
            href="https://apps.shopify.com/"
            className={styles.installBtn}
            target="_blank"
            rel="noreferrer"
          >
            Add to Shopify — it's free →
          </a>
        </div>

        {/* Hero Screenshot */}
        <div className={styles.heroScreenshot}>
          <img
            src="/Tagflow-Screenshot_01.png"
            alt="TagFlow rules dashboard showing active tagging rules"
            className={styles.screenshotImg}
          />
        </div>
      </section>

      {/* Features */}
      <section className={styles.features}>
        <div className={styles.featuresGrid}>
          <div className={styles.featureCard}>
            <div className={styles.featureIcon}>⚡</div>
            <h3>Real-time tagging</h3>
            <p>
              Tags are applied the instant an order is created or updated — via
              Shopify webhooks with zero delay.
            </p>
          </div>
          <div className={styles.featureCard}>
            <div className={styles.featureIcon}>🎯</div>
            <h3>Powerful conditions</h3>
            <p>
              Filter by order value, customer country, SKU, item count, discount
              codes, and customer tags.
            </p>
          </div>
          <div className={styles.featureCard}>
            <div className={styles.featureIcon}>🔧</div>
            <h3>No code required</h3>
            <p>
              Build rules with a simple visual form. Enable, disable, or delete
              rules anytime from your dashboard.
            </p>
          </div>
        </div>
      </section>

      {/* How it works */}
      <section className={styles.steps}>
        <h2 className={styles.sectionHeading}>How it works</h2>
        <div className={styles.stepsGrid}>
          <div className={styles.step}>
            <span className={styles.stepNum}>1</span>
            <div>
              <strong>Create a rule</strong>
              <p>Set IF conditions — order total, country, SKUs, discounts.</p>
            </div>
          </div>
          <div className={styles.step}>
            <span className={styles.stepNum}>2</span>
            <div>
              <strong>Set your tag</strong>
              <p>Choose the tag to apply when conditions are met.</p>
            </div>
          </div>
          <div className={styles.step}>
            <span className={styles.stepNum}>3</span>
            <div>
              <strong>TagFlow does the rest</strong>
              <p>Every new order is checked and tagged automatically.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Screenshots Showcase */}
      <section className={styles.showcase}>
        <h2 className={styles.sectionHeading}>Build powerful rules in seconds</h2>
        <div className={styles.showcaseGrid}>
          <div className={styles.showcaseItem}>
            <img
              src="/Tagflow-Screenshot_02.png"
              alt="Simple rule: Tag wholesale orders with 20+ items"
              className={styles.showcaseImg}
            />
            <p className={styles.showcaseCaption}>
              <strong>Simple rules</strong> — Tag wholesale orders with 20+ items
            </p>
          </div>
          <div className={styles.showcaseItem}>
            <img
              src="/Tagflow-Screenshot_03.png"
              alt="Multi-condition rule: Flag high-risk orders for review"
              className={styles.showcaseImg}
            />
            <p className={styles.showcaseCaption}>
              <strong>Multi-condition rules</strong> — Flag high-risk orders for manual review
            </p>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className={styles.footer}>
        <span>© {new Date().getFullYear()} TagFlow</span>
        <a href="/privacy">Privacy Policy</a>
        <a href="/support">Support</a>
      </footer>
    </div>
  );
}
